import { PaginatedResponse, useApi } from '@/api';
import { SmartActivityList, Page } from '@/components';
import { useSearchParams } from '@/hooks';
import {
  Heading,
  TableSkeleton,
  useTableProps,
  Pagination,
  DropdownFilter,
  NetworkIcon,
  ProtocolIcon,
  CryptoIcon,
  Flex
} from '@blockanalitica/ui';
import type { SmartActivityData } from '@/components/SmartActivityList';
import _ from 'lodash';
import { DropdownFilterOption } from '@blockanalitica/ui/package/types/src/components/molecules/TableFilters/DropdownFilter';
import { useCallback } from 'react';

type Dropdown = DropdownFilterOption & {
  address?: string;
};
type SummaryEventsResponse = PaginatedResponse<SmartActivityData[]> & {
  networks: Dropdown[];
  protocols: Dropdown[];
  assets: Dropdown[];
};

export default function SLLAllocationActivityPage() {
  const [network] = useSearchParams('network', '');
  const { pagination, filters } = useTableProps({
    pageSize: 15,
    initialFilters: {
      // @ts-ignore
      selectedNetwork: network ? [network] : []
    }
  });

  const [data, error] = useApi<SummaryEventsResponse>({
    url: '/sparkstar/sll/summary-events/',
    queryParams: {
      p: pagination.currentPage,
      p_size: pagination.pageSize,
      order: 'datetime',
      network: filters.filtersApplied['selectedNetwork'],
      asset: filters.filtersApplied['selectedAsset'],
      protocol: filters.filtersApplied['selectedProtocol']
    }
  });

  const networkFilterIcons = useCallback((option: Dropdown) => {
    return <NetworkIcon network={option.value as string} size="2" />;
  }, []);
  const protocolFilterIcons = useCallback((option: Dropdown) => {
    return <ProtocolIcon protocol={option.label} size="2" />;
  }, []);
  const assetsFilterIcons = useCallback((option: Dropdown) => {
    return (
      <CryptoIcon
        name={option.label}
        address={option.address}
        network={option.network}
        size="2"
      />
    );
  }, []);

  if (error) {
    throw error;
  }

  if (!data) {
    return <TableSkeleton rows={10} columns={6} />;
  }

  return (
    <Page
      title={<Heading>Allocation Activity</Heading>}
      actions={
        <Flex gap={['s', 'xs']}>
          <DropdownFilter
            multi={true}
            filterField="selectedNetwork"
            options={data.networks || []}
            pagination={pagination}
            titleFormat={(filteredCount: number) =>
              `Networks (${filteredCount})`
            }
            optionPrefixFn={networkFilterIcons}
            {...filters}
          />
          <DropdownFilter
            multi={true}
            filterField="selectedProtocol"
            options={data.protocols || []}
            pagination={pagination}
            titleFormat={(filteredCount: number) =>
              `Protocols (${filteredCount})`
            }
            optionPrefixFn={protocolFilterIcons}
            {...filters}
          />
          <DropdownFilter
            multi={true}
            filterField="selectedAsset"
            options={data.assets || []}
            pagination={pagination}
            titleFormat={(filteredCount: number) =>
              `Underlying Assets (${filteredCount})`
            }
            optionPrefixFn={assetsFilterIcons}
            {...filters}
          />
        </Flex>
      }>
      <SmartActivityList data={data.results} />
      <Pagination {...pagination} totalRecords={data.count} />
    </Page>
  );
}
